// qs-useWebWorkers.ts
import type { PathProcessingData, PathProcessingResult } from '@/workers/qs-worker-path-processing'
import type { TaperingWorkerData, TaperingWorkerResult } from '@/workers/qs-worker-tapering'

interface PendingRequest<T = unknown> {
  resolve: (value: T) => void
  reject: (error: Error) => void
  timestamp: number
  timeoutId?: number
}

interface WorkerMessage {
  type: string
  data: unknown
  id?: string
}

type SerializableValue = string | number | boolean | null | SerializableValue[] | { [key: string]: SerializableValue }

export class WebWorkerManager {
  private taperingWorker: Worker | null = null
  private pathProcessingWorker: Worker | null = null
  private pendingRequests = new Map<string, PendingRequest>()
  private requestId = 0

  constructor() {
    this.initializeWorkers()
  }

  private initializeWorkers(): void {
    try {
      if (typeof Worker === 'undefined') {
        console.warn('Web Workers not supported in this environment')
        return
      }

      this.taperingWorker = new Worker(
        new URL('../workers/qs-worker-tapering.ts', import.meta.url),
        { type: 'module' }
      )
      this.taperingWorker.onmessage = this.handleTaperingWorkerMessage.bind(this)
      this.taperingWorker.onerror = this.handleWorkerError.bind(this)

      this.pathProcessingWorker = new Worker(
        new URL('../workers/qs-worker-path-processing.ts', import.meta.url),
        { type: 'module' }
      )
      this.pathProcessingWorker.onmessage = this.handlePathProcessingWorkerMessage.bind(this)
      this.pathProcessingWorker.onerror = this.handleWorkerError.bind(this)
    } catch (error) {
      console.warn('Web Workers not available, falling back to main thread:', error)
      this.taperingWorker = null
      this.pathProcessingWorker = null
    }
  }

  private handleTaperingWorkerMessage(e: MessageEvent<WorkerMessage>): void {
    const { type, data, id } = e.data

    if (id && this.pendingRequests.has(id)) {
      const request = this.pendingRequests.get(id)!
      this.pendingRequests.delete(id)
      
      if (request.timeoutId) {
        clearTimeout(request.timeoutId)
      }

      if (type === 'TAPER_RESULT') {
        request.resolve(data as TaperingWorkerResult)
      } else if (type === 'ERROR') {
        const errorData = data as { error: string }
        request.reject(new Error(errorData.error))
      }
    }
  }

  private handlePathProcessingWorkerMessage(e: MessageEvent<WorkerMessage>): void {
    const { type, data, id } = e.data

    if (id && this.pendingRequests.has(id)) {
      const request = this.pendingRequests.get(id)!
      this.pendingRequests.delete(id)
      
      if (request.timeoutId) {
        clearTimeout(request.timeoutId)
      }

      if (type === 'PATH_RESULT') {
        request.resolve(data as PathProcessingResult)
      } else if (type === 'ERROR') {
        const errorData = data as { error: string }
        request.reject(new Error(errorData.error))
      }
    }
  }

  private handleWorkerError(error: ErrorEvent): void {
    console.error('Web Worker error:', error)
  }

  private generateRequestId(): string {
    return `req_${++this.requestId}_${Date.now()}`
  }

  private cleanDataForWorker(data: unknown): SerializableValue {
    if (data === null || data === undefined) {
      return data as SerializableValue
    }

    if (Array.isArray(data)) {
      return data.map(item => this.cleanDataForWorker(item))
    }

    if (typeof data === 'object') {
      const cleaned: Record<string, SerializableValue> = {}
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          cleaned[key] = value
        } else if (Array.isArray(value)) {
          cleaned[key] = this.cleanDataForWorker(value)
        } else if (value && typeof value === 'object') {
          cleaned[key] = this.cleanDataForWorker(value)
        }
      }
      return cleaned
    }

    return data as SerializableValue
  }

  private sendWorkerMessage<T>(worker: Worker, message: WorkerMessage): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = this.generateRequestId()

      const timeoutId = window.setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id)
          reject(new Error('Worker request timeout'))
        }
      }, 10000)

      this.pendingRequests.set(id, {
        resolve: (value: unknown) => {
          clearTimeout(timeoutId)
          resolve(value as T)
        },
        reject: (error: Error) => {
          clearTimeout(timeoutId)
          reject(error)
        },
        timestamp: Date.now(),
        timeoutId
      })

      const cleanMessage: WorkerMessage = {
        type: message.type,
        data: this.cleanDataForWorker(message.data),
        id
      }

      worker.postMessage(cleanMessage)
    })
  }

  async calculateTaperedLine(data: TaperingWorkerData): Promise<TaperingWorkerResult> {
    if (!this.taperingWorker) {
      throw new Error('TaperingEngine Worker not available')
    }

    try {
      const result = await this.sendWorkerMessage<TaperingWorkerResult>(this.taperingWorker, {
        type: 'TAPER_CALCULATION',
        data
      })

      return result
    } catch (error) {
      console.error('TaperingEngine Worker error:', error)
      throw error
    }
  }

  async processPath(data: PathProcessingData): Promise<PathProcessingResult> {
    if (!this.pathProcessingWorker) {
      throw new Error('Path-Processing Worker not available')
    }

    try {
      const result = await this.sendWorkerMessage<PathProcessingResult>(this.pathProcessingWorker, {
        type: 'PROCESS_POINTS',
        data
      })

      return result
    } catch (error) {
      console.error('Path-Processing Worker error:', error)
      throw error
    }
  }

  async smoothPath(points: number[], smoothingFactor: number = 0.3): Promise<number[]> {
    if (!this.pathProcessingWorker) {
      throw new Error('Path-Processing Worker not available')
    }

    try {
      const result = await this.sendWorkerMessage<{ processedPoints: number[] }>(this.pathProcessingWorker, {
        type: 'SMOOTH_PATH',
        data: { points, smoothingFactor }
      })

      return result.processedPoints
    } catch (error) {
      console.error('Path-Processing Worker error:', error)
      throw error
    }
  }

  async smoothPathChaikin(points: number[], iterations: number = 1): Promise<number[]> {
    if (!this.pathProcessingWorker) {
      throw new Error('Path-Processing Worker not available')
    }

    try {
      const result = await this.sendWorkerMessage<{ processedPoints: number[] }>(this.pathProcessingWorker, {
        type: 'SMOOTH_PATH_CHAIKIN',
        data: { points, iterations }
      })

      return result.processedPoints
    } catch (error) {
      console.error('Path-Processing Worker error:', error)
      throw error
    }
  }

  private smoothPathMovingAverageSync(points: number[], windowSize: number = 5): number[] {
    if (points.length < 6) return points

    const pointCount = points.length / 2
    const smoothed: number[] = []
    const halfWindow = Math.floor(windowSize / 2)

    for (let i = 0; i < pointCount; i++) {
      let sumX = 0
      let sumY = 0
      let count = 0

      for (let j = -halfWindow; j <= halfWindow; j++) {
        const idx = i + j
        if (idx >= 0 && idx < pointCount) {
          sumX += points[idx * 2]
          sumY += points[idx * 2 + 1]
          count++
        }
      }

      if (count > 0) {
        smoothed.push(sumX / count, sumY / count)
      } else {
        smoothed.push(points[i * 2], points[i * 2 + 1])
      }
    }

    return smoothed
  }

  async smoothPathMovingAverage(points: number[], windowSize: number = 5): Promise<number[]> {
    if (!this.pathProcessingWorker) {
      return this.smoothPathMovingAverageSync(points, windowSize)
    }

    try {
      const result = await this.sendWorkerMessage<{ processedPoints: number[] }>(this.pathProcessingWorker, {
        type: 'SMOOTH_PATH_MOVING_AVERAGE',
        data: { points, windowSize }
      })

      return result.processedPoints
    } catch (error) {
      console.error('Path-Processing Worker error:', error)
      return this.smoothPathMovingAverageSync(points, windowSize)
    }
  }

  async smoothPathCatmullRom(points: number[], samplesPerSegment: number = 5, alpha: number = 0.5): Promise<number[]> {
    if (!this.pathProcessingWorker) {
      throw new Error('Path-Processing Worker not available')
    }

    try {
      const result = await this.sendWorkerMessage<{ processedPoints: number[] }>(this.pathProcessingWorker, {
        type: 'SMOOTH_PATH_CATMULL_ROM',
        data: { points, samplesPerSegment, alpha }
      })

      return result.processedPoints
    } catch (error) {
      console.error('Path-Processing Worker error:', error)
      throw error
    }
  }

  async optimizePath(points: number[], level: 'low' | 'medium' | 'high' = 'medium'): Promise<number[]> {
    if (!this.pathProcessingWorker) {
      throw new Error('Path-Processing Worker not available')
    }

    try {
      const result = await this.sendWorkerMessage<{ processedPoints: number[] }>(this.pathProcessingWorker, {
        type: 'OPTIMIZE_PATH',
        data: { points, optimizationLevel: level }
      })

      return result.processedPoints
    } catch (error) {
      console.error('Path-Processing Worker error:', error)
      throw error
    }
  }

  async clearCaches(): Promise<void> {
    const promises: Promise<void>[] = []

    if (this.taperingWorker) {
      promises.push(
        this.sendWorkerMessage<void>(this.taperingWorker, { type: 'CLEAR_CACHE', data: {} })
          .then(() => {})
          .catch(() => {})
      )
    }

    if (this.pathProcessingWorker) {
      promises.push(
        this.sendWorkerMessage<void>(this.pathProcessingWorker, { type: 'CLEAR_CACHE', data: {} })
          .then(() => {})
          .catch(() => {})
      )
    }

    await Promise.all(promises)
  }

  get isAvailable(): boolean {
    return this.taperingWorker !== null && this.pathProcessingWorker !== null
  }

  getStatus(): {
    taperingWorker: boolean
    pathProcessingWorker: boolean
    pendingRequests: number
  } {
    return {
      taperingWorker: this.taperingWorker !== null,
      pathProcessingWorker: this.pathProcessingWorker !== null,
      pendingRequests: this.pendingRequests.size
    }
  }

  destroy(): void {
    if (this.taperingWorker) {
      this.taperingWorker.terminate()
      this.taperingWorker = null
    }

    if (this.pathProcessingWorker) {
      this.pathProcessingWorker.terminate()
      this.pathProcessingWorker = null
    }

    this.pendingRequests.forEach(({ reject, timeoutId }) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      reject(new Error('Worker destroyed'))
    })
    this.pendingRequests.clear()
  }
}

let workerManager: WebWorkerManager | null = null

export function useWebWorkers(): WebWorkerManager {
  if (!workerManager) {
    workerManager = new WebWorkerManager()
  }
  return workerManager
}

export function cleanupWebWorkers(): void {
  if (workerManager) {
    workerManager.destroy()
    workerManager = null
  }
}